import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';
import { Message } from '../../types';
import { MarkdownRenderer } from './MarkdownRenderer';

interface DebugInfoMessageProps {
  message: Message;
}

export const DebugInfoMessage: React.FC<DebugInfoMessageProps> = ({ message }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyId = (id: string, type: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(type);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Check if this is a "Debug Info" message with debugIds (user prompt debug)
  const hasDebugIds = !!message.debugIds;
  
  // Extract title from content for API call messages (e.g., "**Grading API Call** → ...")
  const getCompactTitle = () => {
    if (hasDebugIds) {
      return (
        <>
          🔧 Debug Info
          <span className="opacity-60">•</span>
          <code className="bg-background px-1 py-0.5 rounded text-[10px]">
            {message.debugIds!.messageId.substring(0, 8)}...
          </code>
        </>
      );
    }
    
    // For API call messages, extract the title from content
    const content = message.content || '';
    const match = content.match(/^\*\*([^*]+)\*\*/);
    if (match) {
      // Get first line as title preview
      const firstLine = content.split('\n')[0];
      return firstLine.replace(/\*\*/g, '');
    }
    
    return '📋 Debug Message';
  };

  return (
    <div className="flex justify-end mb-4">
      <div className="max-w-[85%] bg-muted/30 border border-border/50 rounded-lg overflow-hidden">
        {/* Compact Header - Always Visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-2 flex items-center gap-2 hover:bg-muted/50 transition-colors text-left"
        >
          {isExpanded ? (
            <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          )}
          <span className="text-xs font-mono text-muted-foreground flex items-center gap-2">
            {getCompactTitle()}
          </span>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-3 pb-3 border-t border-border/50">
            <div className="mt-3 space-y-3 text-xs">
              {/* Show debugIds info only if present */}
              {hasDebugIds && (
                <>
                  {/* Message ID */}
                  <div className="flex items-start gap-2">
                    <div className="font-semibold text-foreground min-w-[100px]">Message ID:</div>
                    <div className="flex-1 flex items-center gap-2">
                      <code className="bg-background px-2 py-1 rounded font-mono text-[10px] break-all">
                        {message.debugIds!.messageId}
                      </code>
                      <button
                        onClick={() => handleCopyId(message.debugIds!.messageId, 'message')}
                        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                        title="Copy Message ID"
                      >
                        {copiedId === 'message' ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Conversation ID */}
                  <div className="flex items-start gap-2">
                    <div className="font-semibold text-foreground min-w-[100px]">Conversation ID:</div>
                    <div className="flex-1 flex items-center gap-2">
                      <code className="bg-background px-2 py-1 rounded font-mono text-[10px] break-all">
                        {message.debugIds!.conversationId}
                      </code>
                      <button
                        onClick={() => handleCopyId(message.debugIds!.conversationId, 'conversation')}
                        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                        title="Copy Conversation ID"
                      >
                        {copiedId === 'conversation' ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div className="flex items-start gap-2">
                    <div className="font-semibold text-foreground min-w-[100px]">Timestamp:</div>
                    <div className="text-muted-foreground font-mono text-[10px]">
                      {new Date(message.debugIds!.timestamp).toLocaleString()}
                    </div>
                  </div>
                </>
              )}

              {/* Content - for debugIds messages, strip the header; for others, show full content */}
              <div className={hasDebugIds ? "mt-4 pt-3 border-t border-border/30" : ""}>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <MarkdownRenderer 
                    content={hasDebugIds 
                      ? message.content.replace('🔧 **Debug Info**\n\n', '')
                      : message.content
                    } 
                    className="text-xs"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


